import { useState, type FormEvent } from 'react';
import { DraftNotice } from '../../components/DraftNotice';
import { Modal } from '../../components/Modal';
import { planErrorMessage } from '../../components/plan/planErrors';
import { TicketHead } from '../../components/TicketHead';
import { TokenListEditor } from '../../components/TokenListEditor';
import { clearDraft, knowledgeBaseDraftKey, restoreDraft } from '../../lib/draftStorage';
import { formatDate } from '../../lib/format';
import { useDraft } from '../../lib/useDraft';
import type { ProductCreate, ProductRead, ProductStatus } from '../../types/api';
import { productsApi } from './knowledgeBaseApi';
import { SectionShell } from './SectionShell';
import { useCollection } from './useCollection';
import { useSectionCapacity } from './useSectionCapacity';
import { useBrandCopy } from '../onboarding/useBrandCopy';

const STATUS_OPTIONS: readonly { value: ProductStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'discontinued', label: 'Discontinued' },
];

/** Decimal string, e.g. `19`, `19.5`, `19.99`. */
const PRICE_PATTERN = /^\d+(\.\d{1,2})?$/;

function statusLabel(status: ProductStatus): string {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

/** Autosaved shape. Field names are the form's, not the API's. */
interface ProductDraft {
  name: string;
  description: string;
  price: string;
  benefits: string[];
  status: ProductStatus;
  launchDate: string;
}

function productDraftKey(businessId: string, item: ProductRead | null): string {
  return knowledgeBaseDraftKey('products', businessId, item?.id ?? null);
}

/**
 * A restored draft is untrusted input, and the shape check only proves `status`
 * is a string. A value the select does not offer falls back to the saved one.
 */
function safeStatus(candidate: ProductStatus, fallback: ProductStatus): ProductStatus {
  return STATUS_OPTIONS.some((option) => option.value === candidate) ? candidate : fallback;
}

interface ProductFormProps {
  businessId: string;
  initial: ProductRead | null;
  isSaving: boolean;
  error: string | null;
  onSubmit: (body: ProductCreate) => void;
  onCancel: () => void;
}

function ProductForm({
  businessId,
  initial,
  isSaving,
  error,
  onSubmit,
  onCancel,
}: ProductFormProps) {
  const { singular } = useBrandCopy().records.products;
  const draftKey = productDraftKey(businessId, initial);
  const pristine: ProductDraft = {
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    price: initial?.price ?? '',
    benefits: initial?.benefits ?? [],
    status: initial?.status ?? 'draft',
    launchDate: initial?.launch_date ?? '',
  };
  // The form mounts when the modal opens, so this initialiser is the "on open"
  // hook — no effect, no second render.
  const [restored] = useState(() => restoreDraft(draftKey, pristine));
  const [name, setName] = useState(restored.value.name);
  const [description, setDescription] = useState(restored.value.description);
  const [price, setPrice] = useState(restored.value.price);
  const [benefits, setBenefits] = useState<string[]>(restored.value.benefits);
  const [status, setStatus] = useState<ProductStatus>(() =>
    safeStatus(restored.value.status, pristine.status),
  );
  const [launchDate, setLaunchDate] = useState(restored.value.launchDate);
  const [restoredAt, setRestoredAt] = useState<string | null>(restored.savedAt);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useDraft(draftKey, { name, description, price, benefits, status, launchDate }, pristine);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) {
      setFieldError('Give the product a name.');
      return;
    }
    if (price.trim() && !PRICE_PATTERN.test(price.trim())) {
      setFieldError('Price must be a plain number such as 19.99 — no currency symbol.');
      return;
    }
    setFieldError(null);
    onSubmit({
      name: name.trim(),
      description: description.trim() || null,
      price: price.trim() || null,
      benefits,
      status,
      launch_date: launchDate || null,
    });
  }

  /**
   * Cancel is a decision to throw the work away. Escape and the header Close
   * are not — those leave the draft where it is.
   */
  function handleCancel() {
    clearDraft(draftKey);
    onCancel();
  }

  const message = fieldError ?? error;

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      {restoredAt ? (
        <DraftNotice savedAt={restoredAt} onDismiss={() => setRestoredAt(null)} />
      ) : null}

      <p className="kb-form__hint">Name, what it is, and the price if you want it mentioned.</p>

      <label className="field">
        <span className="field__label">Name</span>
        <input
          className="field__input"
          type="text"
          maxLength={200}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      <label className="field">
        <span className="field__label">Description</span>
        <textarea
          className="field__input"
          rows={4}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <div className="kb-form__row">
        <label className="field">
          <span className="field__label">Price</span>
          <input
            className="field__input"
            type="text"
            inputMode="decimal"
            placeholder="19.99"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
          />
          <span className="field__hint">Number only. Leave blank if it varies.</span>
        </label>

        <label className="field">
          <span className="field__label">Status</span>
          <select
            className="field__input"
            value={status}
            onChange={(event) => setStatus(event.target.value as ProductStatus)}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Launch date</span>
          <input
            className="field__input"
            type="date"
            value={launchDate}
            onChange={(event) => setLaunchDate(event.target.value)}
          />
        </label>
      </div>

      <TokenListEditor
        label="Benefits"
        hint="One claim per entry. We check posts against these."
        placeholder="Roasted within 48 hours of shipping"
        addLabel="Add benefit"
        tokens={benefits}
        onChange={setBenefits}
      />

      {message ? (
        <p className="form__error" role="alert">
          {message}
        </p>
      ) : null}

      <div className="modal__actions">
        <button className="button" type="button" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </button>
        <button className="button button--primary" type="submit" disabled={isSaving}>
          {isSaving ? 'Saving' : `Save ${singular}`}
        </button>
      </div>
    </form>
  );
}

/** Products — structured records the generator can cite, not a free-text blob. */
export function ProductsSection({ businessId }: { businessId: string }) {
  const collection = useCollection(productsApi, businessId, 'products');
  const { capacity, isFull, planName, fullTitle, refreshCapacity } = useSectionCapacity(
    businessId,
    'products',
  );
  const copy = useBrandCopy();
  const [editing, setEditing] = useState<ProductRead | null | 'new'>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  function startCreate() {
    setWriteError(null);
    setEditing('new');
  }

  async function handleSubmit(body: ProductCreate) {
    const draftKey = productDraftKey(businessId, editing === 'new' ? null : editing);
    setIsSaving(true);
    setWriteError(null);
    try {
      if (editing && editing !== 'new') {
        await collection.update(editing.id, body);
      } else {
        await collection.create(body);
        refreshCapacity();
      }
      // The record is on file; the local copy has nothing left to protect.
      clearDraft(draftKey);
      setEditing(null);
    } catch (caught) {
      // A 402 at the cap: the counts were stale, so re-read them too.
      refreshCapacity();
      setWriteError(
        planErrorMessage(caught, `That ${copy.records.products.singular} did not save.`, {
          plan: planName,
          subject: copy.sections.products,
        }),
      );
    } finally {
      setIsSaving(false);
    }
  }

  /** Optimistic: the row goes now, the DELETE waits out the undo window. */
  function startDelete(item: ProductRead) {
    collection.removeWithUndo({
      id: item.id,
      label: item.name,
      failureMessage: `That ${copy.records.products.singular} could not be deleted.`,
      onRemoved: refreshCapacity,
    });
  }

  return (
    <>
      <SectionShell
        title={copy.sections.products}
        lede={copy.records.products.lede}
        action={
          <button
            className="button button--primary"
            type="button"
            onClick={startCreate}
            disabled={isFull}
            title={fullTitle}
          >
            {copy.records.products.add}
          </button>
        }
        capacity={capacity}
        isLoading={collection.isLoading}
        error={collection.error ?? collection.deleteError}
        isEmpty={collection.items.length === 0}
        emptyMessage={copy.records.products.empty}
        emptyAction={
          <button
            className="button"
            type="button"
            onClick={startCreate}
            disabled={isFull}
            title={fullTitle}
          >
            {copy.records.products.add}
          </button>
        }
      >
        <ul className="record-list">
          {collection.items.map((item) => (
            <li className="record" key={item.id}>
              <TicketHead label={copy.records.products.singular} timestamp={item.created_at} />
              <div className="record__head">
                <span className="record__title">{item.name}</span>
                <span className={`badge badge--${item.status}`}>{statusLabel(item.status)}</span>
              </div>
              <div className="record__facts">
                {item.price ? <span className="record__meta">{item.price}</span> : null}
                {item.launch_date ? (
                  <span className="record__meta">Launches {formatDate(item.launch_date)}</span>
                ) : null}
              </div>
              {item.description ? <p className="record__body">{item.description}</p> : null}
              {item.benefits.length > 0 ? (
                <ul className="token-list token-list--static">
                  {item.benefits.map((benefit) => (
                    <li className="token" key={benefit}>
                      {benefit}
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="record__actions">
                <button
                  className="button button--ghost"
                  type="button"
                  onClick={() => {
                    setWriteError(null);
                    setEditing(item);
                  }}
                >
                  Edit
                </button>
                <button
                  className="button button--ghost button--danger-text"
                  type="button"
                  onClick={() => startDelete(item)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </SectionShell>

      {editing ? (
        <Modal
          title={`${editing === 'new' ? 'New' : 'Edit'} ${copy.records.products.singular}`}
          kind={editing === 'new' ? 'new product' : 'product'}
          onDismiss={() => setEditing(null)}
        >
          <ProductForm
            businessId={businessId}
            initial={editing === 'new' ? null : editing}
            isSaving={isSaving}
            error={writeError}
            onSubmit={(body) => void handleSubmit(body)}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      ) : null}
    </>
  );
}
